/**
 * Route loaders call these so data starts loading in parallel with the screen's code, and on hover
 * (links preload on intent). Keys and fetchers match the hooks in @/lib/api, so the screen's own
 * queries find the data already in the cache.
 */
import type { QueryClient } from "@tanstack/react-query";
import { api, qk, unwrap, type LogFilter, type MetricsFilter } from "@/lib/api";

export function prefetchInstances(qc: QueryClient, project: string, environment: string) {
  void qc.prefetchQuery({
    queryKey: qk.serviceInstances(project, environment),
    queryFn: () => unwrap(api.GET("/v1/projects/{project}/environments/{environment}/services", { params: { path: { project, environment } } })),
  });
}

export function prefetchLogs(qc: QueryClient, project: string, filter: LogFilter) {
  void qc.prefetchQuery({
    queryKey: qk.logs(project, filter),
    queryFn: () => unwrap(api.GET("/v1/projects/{project}/logs", { params: { path: { project }, query: filter } })),
  });
}

export function prefetchMetrics(qc: QueryClient, project: string, filter: MetricsFilter) {
  void qc.prefetchQuery({
    queryKey: qk.metrics(project, filter),
    queryFn: () => unwrap(api.GET("/v1/projects/{project}/metrics", { params: { path: { project }, query: filter } })),
  });
}

export function prefetchSafety(qc: QueryClient, project: string, environment: string) {
  void qc.prefetchQuery({
    queryKey: qk.safety(project, environment),
    queryFn: () => unwrap(api.GET("/v1/projects/{project}/safety", { params: { path: { project }, query: { environment } } })),
  });
  void qc.prefetchQuery({
    queryKey: qk.project(project),
    queryFn: () => unwrap(api.GET("/v1/projects/{project}", { params: { path: { project } } })),
  });
}

/** "Now" rounded to the minute, as the metrics screen uses it, so the prefetched key matches. */
export const minuteNow = () => Math.floor(Date.now() / 60_000) * 60_000;
